import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

const MediaViewPage = () => {
  const { albumId, photoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const photos = location.state?.photos || [];

  const currentIndex = parseInt(photoId, 10);
  const photo = photos[currentIndex];

  const handleDownload = async () => {
    if (!photo) return;
    try {
      // Fetch the media as a blob to bypass CORS restrictions
      const response = await fetch(photo.url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = photo.name || 'media';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading media:', error);
      // Fallback to opening in new tab
      window.open(photo.url, '_blank');
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      navigate(`/album/${albumId}/photo/${currentIndex - 1}`, { state: { photos }, replace: true });
    }
  };

  const handleNext = () => {
    if (currentIndex < photos.length - 1) {
      navigate(`/album/${albumId}/photo/${currentIndex + 1}`, { state: { photos }, replace: true });
    }
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      switch (e.key) {
        case 'Escape':
          handleBack();
          break;
        case 'ArrowLeft':
          if (currentIndex > 0) handlePrev();
          break;
        case 'ArrowRight':
          if (currentIndex < photos.length - 1) handleNext();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [albumId, currentIndex, photos.length]);

  if (!photo) return null;

  return (
    <div className="fixed inset-0 bg-black flex flex-col z-[2000]">
      {/* Header with controls */}
      <div className="flex items-center justify-between p-4 bg-black/50 backdrop-blur-sm">
        <button
          onClick={handleBack}
          className="p-2 bg-transparent border-none text-white cursor-pointer flex items-center gap-2 hover:bg-white/10 rounded transition-colors"
        >
          <span className="material-icons">arrow_back</span>
          <span className="hidden sm:inline">Back</span>
        </button>

        <span className="text-white text-sm opacity-80">{photo.name}</span>

        <button
          onClick={handleDownload}
          className="p-2 bg-transparent border-none text-white cursor-pointer flex items-center gap-2 hover:bg-white/10 rounded transition-colors"
        >
          <span className="material-icons">download</span>
          <span className="hidden sm:inline">Download</span>
        </button>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        {/* Previous button */}
        {currentIndex > 0 && (
          <button
            onClick={handlePrev}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 backdrop-blur-sm border-none text-white rounded-full cursor-pointer flex items-center justify-center z-10 hover:bg-black/70 transition-colors"
          >
            <span className="material-icons text-3xl">chevron_left</span>
          </button>
        )}

        {/* Media content */}
        <div className="w-full h-full flex items-center justify-center p-4">
          {photo.isVideo ? (
            <video
              src={photo.url}
              controls
              autoPlay
              className="max-w-full max-h-full w-auto h-auto"
              style={{ maxHeight: 'calc(100vh - 80px)' }}
            >
              Your browser does not support the video tag.
            </video>
          ) : (
            <img
              src={photo.url}
              alt={photo.name}
              className="max-w-full max-h-full w-auto h-auto object-contain"
              style={{ maxHeight: 'calc(100vh - 80px)' }}
            />
          )}
        </div>

        {/* Next button */}
        {currentIndex < photos.length - 1 && (
          <button
            onClick={handleNext}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 backdrop-blur-sm border-none text-white rounded-full cursor-pointer flex items-center justify-center z-10 hover:bg-black/70 transition-colors"
          >
            <span className="material-icons text-3xl">chevron_right</span>
          </button>
        )}
      </div>

      {/* Navigation indicator (optional) */}
      {photos && photos.length > 1 && (
        <div className="p-2 text-center bg-black/50 backdrop-blur-sm">
          <span className="text-white text-sm opacity-80">
            {currentIndex + 1} / {photos.length}
          </span>
        </div>
      )}
    </div>
  );
};

export default MediaViewPage;
